import React from "react";
import { useHistory } from "react-router-dom";
import { useTheme as useGlobalTheme } from "../../store/contexts/ThemeContext";
import { ROUTES } from "../../routes/User/paths";
import PublicFooter from "../../components/PublicFooter/PublicFooter";
import ScrollToTop from "../../components/ScrollToTop/ScrollToTop";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Fade,
  Button,
  IconButton,
} from "@mui/material";
import {
  VideoCall,
  Security,
  People,
  TrendingUp,
  Verified,
  Lightbulb,
  Favorite,
  Public,
  EmojiEvents,
  Star,
  ArrowBack,
  LightMode,
  DarkMode,
} from "@mui/icons-material";
import "./About.css";

const About = () => {
  const history = useHistory();
  const { darkMode, toggleTheme } = useGlobalTheme();

  const values = [
    {
      icon: <Lightbulb sx={{ fontSize: 40 }} />,
      title: "Innovation First",
      description: "We constantly push boundaries to deliver cutting-edge features that empower creators.",
      color: "#f59e0b",
    },
    {
      icon: <People sx={{ fontSize: 40 }} />,
      title: "Community Driven",
      description: "Our users shape our platform. We listen, adapt, and grow together with our community.",
      color: "#8b5cf6",
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: "Trust & Safety",
      description: "Bank-level security and robust moderation tools keep our platform safe for everyone.",
      color: "#10b981",
    },
    {
      icon: <Favorite sx={{ fontSize: 40 }} />,
      title: "Creator Success",
      description: "We're committed to helping creators monetize their passion and build sustainable income.",
      color: "#ec4899",
    },
  ];

  const milestones = [
    { year: "2024", event: "Platform Founded", description: "IOTA was born from a vision to connect creators worldwide" },
    { year: "2025", event: "10K Users", description: "Reached 10,000 active creators on the platform" },
    { year: "2025", event: "$100K+ Paid", description: "Paid out over $100,000 to our creator community" },
    { year: "2026", event: "Global Expansion", description: "Expanding to serve creators in 50+ countries" },
  ];

  const team = [
    { name: "Innovation Team", role: "Product Development", icon: "🚀" },
    { name: "Creator Support", role: "Community Success", icon: "💬" },
    { name: "Security Team", role: "Platform Safety", icon: "🔒" },
    { name: "Growth Team", role: "Business Development", icon: "📈" },
  ];

  const stats = [
    { value: "10K+", label: "Active Creators", icon: <People /> },
    { value: "50K+", label: "Rooms Hosted", icon: <VideoCall /> },
    { value: "$100K+", label: "Creator Earnings", icon: <TrendingUp /> },
    { value: "99.9%", label: "Uptime", icon: <Verified /> },
  ];

  return (
    <Box className={`about-wrapper ${darkMode ? "dark" : "light"}`}>
      {/* Gradient Mesh Background */}
      <Box className="gradient-mesh">
        <Box className="gradient-blob blob-1" />
        <Box className="gradient-blob blob-2" />
        <Box className="gradient-blob blob-3" />
      </Box>

      {/* Navigation */}
      <Box className="navbar">
        <Container maxWidth="xl">
          <Box className="nav-content">
            <Button
              className="back-btn"
              startIcon={<ArrowBack />}
              onClick={() => history.push(ROUTES.landing)}
            >
              Back to Home
            </Button>
            <Box className="nav-actions">
              <IconButton onClick={toggleTheme} className="theme-toggle-btn" size="small">
                {darkMode ? <LightMode /> : <DarkMode />}
              </IconButton>
              <Button variant="text" className="nav-login" onClick={() => history.push(ROUTES.login)}>
                Log In
              </Button>
              <Button variant="contained" className="nav-signup" onClick={() => history.push(ROUTES.register)}>
                Get Started
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box className="hero-section">
        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Box className="hero-content">
              <Chip
                icon={<Public />}
                label="About IOTA"
                className="hero-chip"
              />
              <Typography variant="h1" className="hero-title">
                Empowering Creators
                <br />
                <span className="gradient-text">Worldwide</span>
              </Typography>
              <Typography variant="h5" className="hero-subtitle">
                We're building the future of live video collaboration, where creativity meets
                <br />
                community and every creator has the tools to succeed.
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box className="stats-section">
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box className="stat-card">
                  <Box className="stat-icon">{stat.icon}</Box>
                  <Typography variant="h3" className="stat-value">
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" className="stat-label">
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box className="mission-section">
        <Container maxWidth="md">
          <Box className="mission-content">
            <Chip label="Our Mission" className="section-chip" />
            <Typography variant="h2" className="section-title">
              Building Connections That <span className="gradient-text">Matter</span>
            </Typography>
            <br/>
            <Typography variant="h6" className="mission-text">
              IOTA was created with a simple yet powerful vision: to democratize live video hosting
              and give every creator the tools they need to build thriving communities while earning
              sustainable income.
            </Typography>
            <br/>

            <Typography variant="body1" className="mission-text">
              We believe that everyone has something valuable to share. Whether you're a gamer building
              a squad, an educator hosting study sessions, a professional leading team meetings, or
              an artist connecting with fans – IOTA provides the platform, tools, and monetization
              options to help you succeed.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Values Section */}
      <Box className="values-section">
        <Container maxWidth="lg">
          <Box className="section-header">
            <Chip label="Our Values" className="section-chip" />
            <Typography variant="h2" className="section-title">
              What We <span className="gradient-text">Stand For</span>
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card className="value-card">
                  <Box
                    className="value-icon"
                    sx={{ background: `linear-gradient(135deg, ${value.color} 0%, ${value.color}dd 100%)` }}
                  >
                    {value.icon}
                  </Box>
                  <CardContent>
                    <Typography variant="h5" className="value-title">
                      {value.title}
                    </Typography>
                    <Typography variant="body1" className="value-description">
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Timeline Section */}
      <Box className="timeline-section">
        <Container maxWidth="lg">
          <Box className="section-header">
            <Chip label="Our Journey" className="section-chip" />
            <Typography variant="h2" className="section-title">
              Growing <span className="gradient-text">Together</span>
            </Typography>
          </Box>

          <Box className="timeline">
            {milestones.map((milestone, index) => (
              <Box key={index} className="timeline-item">
                <Box className="timeline-marker">
                  <Star sx={{ fontSize: 24 }} />
                </Box>
                <Box className="timeline-content">
                  <Typography variant="h4" className="timeline-year">
                    {milestone.year}
                  </Typography>
                  <Typography variant="h5" className="timeline-event">
                    {milestone.event}
                  </Typography>
                  <Typography variant="body1" className="timeline-description">
                    {milestone.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Team Section */}
      <Box className="team-section">
        <Container maxWidth="lg">
          <Box className="section-header">
            <Chip label="Our Team" className="section-chip" />
            <Typography variant="h2" className="section-title">
              Meet the <span className="gradient-text">People</span> Behind IOTA
            </Typography>
            <Typography variant="h6" className="gradient-text">
              A passionate team dedicated to creator success
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {team.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card className="team-card">
                  <Avatar className="team-avatar">{member.icon}</Avatar>
                  <CardContent className="team-content">
                    <Typography variant="h6" className="team-name">
                      {member.name}
                    </Typography>
                    <Typography variant="body2" className="team-role">
                      {member.role}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box className="cta-section">
        <Container maxWidth="md">
          <Box className="cta-content">
            <EmojiEvents sx={{ fontSize: 60, color: "white", mb: 3 }} />
            <Typography variant="h2" className="cta-title">
              Join Our Creator Community
            </Typography>
            <Typography variant="h6" className="cta-subtitle">
              Start building your audience and earning today. It's free to get started.
            </Typography>
            <Button
              variant="contained"
              size="large"
              className="cta-button"
              onClick={() => history.push(ROUTES.register)}
            >
              Get Started Free
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <PublicFooter />

      {/* Scroll to Top */}
      <ScrollToTop />
    </Box>
  );
};

export default About;
